import {
  blockMetaInputSchema,
  dateTypeSchema,
  emailSchema,
  nameSchema,
  objectIdSchema,
  partyRoleSchema,
  paginationWithSearchSchema,
  PartyRole,
  statusCommonSchema,
  StatusCommon,
  User,
  userTypeUnifiedSchema,
} from '@hero/schema';
import { API } from '../types';
import { z } from 'zod';

// ─── userClass — "network shape" bucket ──────────────────────────────────────

/**
 * A "network shape" bucket for a user row. Auth-v2 introduces this
 * so the admin Users page can filter by the multi-org / individual /
 * freelancer axis without leaking the underlying `orgs.length` +
 * `roles` combo into every table filter.
 *
 * - `org_member` — the user belongs to at least one Org.
 * - `individual` — no orgs and `roles` includes `individual_customer`.
 * - `freelancer_driver` — no orgs and `roles` includes `driver`.
 * - `multi_org` — the user is a member of two or more Orgs.
 * - `blocked` — the user's `status === 'blocked'`. Included in the
 *   same enum because the Users page ships a single "class" dropdown
 *   and blocked users are the top-of-mind bucket for admins; readers
 *   who want a strict split can still filter on `status` separately.
 */
export const userClassSchema = z.enum([
  'all',
  'org_member',
  'individual',
  'freelancer_driver',
  'multi_org',
  'blocked',
]);
export type UserClass = z.infer<typeof userClassSchema>;

// ─── get one ─────────────────────────────────────────────────────────────────

export const userGetOneAPISchema = z.object({
  _id: objectIdSchema,
});

export type UserGetOneAPI = API<z.infer<typeof userGetOneAPISchema>, User>;

// ─── get many ────────────────────────────────────────────────────────────────

/**
 * Query filter for the admin Users list. Every filter is optional and
 * translated to a Mongo query fragment by `UserQueryService.transformQueryAPI`
 * on the backend.
 *
 * `orgId` scopes the result to a single Org — used by the Members
 * projection and (for super-admins) by the Users-in-org drawer. Omit
 * to see every user in the network.
 */
export const userGetManyAPISchema = paginationWithSearchSchema.extend({
  /** filter by organization ID — super admin only; normal users are always scoped to their own org */
  orgId: objectIdSchema.optional(),
  /** filter by unified user type (admin / dispatcher / employee / driver) — maps to PartyRole at the DB level */
  userTypeUnified: userTypeUnifiedSchema.optional(),
  /** filter to a single account status */
  status: statusCommonSchema.optional(),
  /** filter to accounts whose status is any of these values (Mongo `$in`); ignored when `status` is set */
  statusIn: z.array(statusCommonSchema).optional(),
  /** filter to users whose `roles[]` (`PartyRole[]`) contains any of these values */
  roles: z.array(partyRoleSchema).optional(),
  /** "network shape" bucket; see `userClassSchema` for the semantics */
  userClass: userClassSchema.optional(),
  /** window filter on `doc.createdAt` (`this_week` / `this_month` / ...) */
  dateType: dateTypeSchema.optional(),
  /**
   * Window filter on `meta.lastSeenOnline.at` (`today` / `yesterday` /
   * `this_week` / …). Uses the shared `mapSimpleDateFilter` helper —
   * same vocabulary as order `simpleDate` / invoice `dueDate`.
   */
  lastSeenOnlineDate: dateTypeSchema.optional(),
});

export type UserGetManyAPI = API<z.infer<typeof userGetManyAPISchema>, User[]>;

// ─── update ──────────────────────────────────────────────────────────────────

/**
 * Update endpoint for a User row. Auth-v2: `status` is settable ONLY
 * by super-admins; the controller enforces this. Non-status fields
 * left open here so the shape can grow (e.g. `preferredLanguage`
 * updates from an admin flow) without churn on the client.
 */
export const userUpdateAPISchema = z.object({
  _id: objectIdSchema,
  /** account status — settable only by super-admin */
  status: statusCommonSchema.optional(),
  /** Required when `status === 'blocked'`. Cleared server-side on activate. */
  blockMeta: blockMetaInputSchema.optional(),
  /** Staff correcting the person's name / email. The phone is the login and is not editable here. */
  name: nameSchema.optional(),
  email: emailSchema.optional(),
});

export type UserUpdateAPI = API<z.infer<typeof userUpdateAPISchema>, User>;

// ─── delete account ──────────────────────────────────────────────────────────

export const userDeleteAccountAPISchema = z.object({
  _id: objectIdSchema,
});

export type UserDeleteAccountAPIv2 = API<undefined, { success: boolean }>;

// ─── analytics ───────────────────────────────────────────────────────────────

/** Query schema for `GET /analytics/users`. */
export const analyticsUsersAPISchema = z.object({
  /** window used for `signups` + `activeInLast7Days` (`activeInLast7Days` overrides window server-side). */
  dateType: dateTypeSchema.default('this_month'),
});
export type AnalyticsUsersQuery = z.infer<typeof analyticsUsersAPISchema>;

/**
 * Response payload for `GET /analytics/users`. Powers the admin
 * Users page's analytics tile (mirrors `AnalyticsLeadsData`'s shape).
 */
export type AnalyticsUsersData = {
  /** Total users in the collection (matching the optional filters). */
  total: number;
  /** Users created in the window. */
  signups: number;
  /** Users grouped by `status`. */
  byStatus: Record<StatusCommon, number>;
  /** Users grouped by any PartyRole they hold. A user with two roles counts in both buckets. */
  byRole: Record<PartyRole, number>;
  /** Users grouped by `userClass` (org_member / individual / freelancer_driver / multi_org / blocked). */
  byClass: Record<Exclude<UserClass, 'all'>, number>;
  /** Users with a `lastSignIn` in the past 7 days. */
  activeInLast7Days: number;
};

export type AnalyticsUsersAPI = API<AnalyticsUsersQuery, AnalyticsUsersData>;
